import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SSOCompletePage from "@/pages/SSOCompletePage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DemoPage from "@/pages/DemoPage";
import WhyKStreakPage from "@/pages/WhyKStreakPage";
import DashboardPage from "@/pages/DashboardPage";
import ChatPage from "@/pages/ChatPage";
import NotificationPrefsPage from "@/pages/NotificationPrefsPage";
import AdminKTTriggersPage from "@/pages/AdminKTTriggersPage";
import UsersPage from "@/pages/UsersPage";
import CalendarPage from "@/pages/CalendarPage";
import TasksPage from "@/pages/TasksPage";
import ArticlesPage from "@/pages/ArticlesPage";
import InboxPage from "@/pages/InboxPage";
import WorkspacePage from "@/pages/WorkspacePage";
import SettingsPage from "@/pages/SettingsPage";
import SearchPage from "@/pages/SearchPage";
import VoiceJournalPage from "@/pages/VoiceJournalPage";
import ExpertiseLocatorPage from "@/pages/ExpertiseLocatorPage";
import KnowledgeTransferPage from "@/pages/KnowledgeTransferPage";
import CapabilityMetricsPage from "@/pages/CapabilityMetricsPage";
import KnowledgeMappingPage from "@/pages/KnowledgeMappingPage";
import ReportsPage from "@/pages/ReportsPage";
import AnalyticsDashboardPage from "@/pages/AnalyticsDashboardPage";
import TeamsPage from "@/pages/TeamsPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import WebinarsPage from "@/pages/WebinarsPage";
import KnowledgeAuditPage from "@/pages/KnowledgeAuditPage";
import CompliancePage from "@/pages/CompliancePage";
import AutomationsPage from "@/pages/AutomationsPage";
import AutomationRunsLogPage from "@/pages/AutomationRunsLogPage";
import PublicAnalyticsSnapshotPage from "@/pages/PublicAnalyticsSnapshotPage";
import EmailConfigPage from "@/pages/EmailConfigPage";
import CertRenewalPipelinePage from "@/pages/CertRenewalPipelinePage";
import KnowledgeDegradationPage from "@/pages/KnowledgeDegradationPage";
import KnowledgeDepsPage from "@/pages/KnowledgeDepsPage";
import KnowledgePredictionsPage from "@/pages/KnowledgePredictionsPage";
import PKMPage from "@/pages/PKMPage";
import OntologyPage from "@/pages/OntologyPage";
import WhatsNewPage from "@/pages/WhatsNewPage";
import AdminChangelogPage from "@/pages/AdminChangelogPage";
import AdminBenchmarksPage from "@/pages/AdminBenchmarksPage";
import AdminSSOPage from "@/pages/AdminSSOPage";
import AdminDomainMigrationPage from "@/pages/AdminDomainMigrationPage";
import AtlasPage from "@/pages/AtlasPage";
import HRWorkflowsPage from "@/pages/HRWorkflowsPage";
import TrainingPage from "@/pages/TrainingPage";
import CoPPage from "@/pages/CoPPage";
import AuthCallback from "@/pages/AuthCallback";
import MobileAppPage from "@/pages/MobileAppPage";

// Layout
import MainLayout from "@/components/layout/MainLayout";

// Components
import KnowledgeAssistant from "@/components/KnowledgeAssistant";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

// Service Worker
import { register as registerSW } from "@/serviceWorkerRegistration";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { GuidedTourProvider } from "@/context/GuidedTourContext";
import { SystemUpdateProvider } from "@/context/SystemUpdateContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [hasConsented, setHasConsented] = useState(() => localStorage.getItem('kstreak_consent') === 'true');
  const [consentChecked, setConsentChecked] = useState(false);

  useEffect(() => {
    if (!user && !loading) {
      checkAuth();
    }
  }, [user, loading, checkAuth]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#4B5563]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasConsented) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="consent-banner">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
          {/* Header */}
          <div className="bg-[#002FA7] px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/15 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <div>
                <h2 className="text-white text-lg font-semibold">Security & Data Notice</h2>
                <p className="text-white/60 text-xs">Please review before proceeding</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            <div className="space-y-3 text-sm text-[#4B5563] leading-relaxed">
              <p>
                Welcome to <span className="font-semibold text-[#0A0A0A]">KStreak</span>. By accessing this platform, you acknowledge the following:
              </p>
              <div className="bg-[#FFF8F0] border border-[#FF9500]/20 rounded-lg p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#FF9500] mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                  <p className="text-xs"><strong>Proprietary Information:</strong> All data, documents, knowledge assets, and content within this platform are proprietary and confidential. Unauthorized access, distribution, copying, or disclosure of any information is strictly prohibited.</p>
                </div>
                <div className="flex items-start gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#FF9500] mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  <p className="text-xs"><strong>Data Protection:</strong> You are responsible for safeguarding your credentials and any information accessed through this platform. Do not share your login or session with unauthorized individuals.</p>
                </div>
                <div className="flex items-start gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#FF9500] mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/></svg>
                  <p className="text-xs"><strong>Monitoring & Compliance:</strong> Activity on this platform may be monitored for security and compliance purposes. By proceeding, you consent to this monitoring.</p>
                </div>
              </div>
            </div>

            {/* Consent Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group p-3 rounded-lg border border-[#E5E7EB] hover:border-[#002FA7]/30 transition-colors" data-testid="consent-checkbox-label">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-[#D1D5DB] text-[#002FA7] focus:ring-[#002FA7] cursor-pointer"
                data-testid="consent-checkbox"
              />
              <span className="text-xs text-[#4B5563] leading-relaxed">
                I acknowledge that all information on this platform is proprietary and confidential. I agree to comply with all applicable data protection and security policies, and I consent to platform activity monitoring.
              </span>
            </label>
          </div>

          {/* Footer */}
          <div className="px-6 pb-5 flex items-center justify-between">
            <button
              onClick={() => { localStorage.removeItem('kstreak_token'); window.location.href = '/login'; }}
              className="text-xs text-[#9CA3AF] hover:text-[#4B5563] transition-colors"
              data-testid="consent-decline-btn"
            >
              Decline & Sign Out
            </button>
            <button
              onClick={() => {
                if (consentChecked) {
                  localStorage.setItem('kstreak_consent', 'true');
                  setHasConsented(true);
                }
              }}
              disabled={!consentChecked}
              className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
                consentChecked
                  ? 'bg-[#002FA7] text-white hover:bg-[#00227A] shadow-md'
                  : 'bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
              }`}
              data-testid="consent-accept-btn"
            >
              Accept & Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <MainLayout>{children}</MainLayout>;
};

// Route wrapper that additionally enforces role-based access.
// Non-privileged users are redirected to /dashboard with a toast.
const RoleGatedRoute = ({ children, allowedRoles }) => {
  const { user } = useAuth();
  if (user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const STAFF_ROLES = ['admin', 'manager', 'hr'];

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check for session_id in URL hash BEFORE any other routing
  // This prevents race conditions with auth checks
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/atlas" element={<AtlasPage />} />
      <Route path="/atlas/mini/:miniId" element={<AtlasPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/sso/complete" element={<SSOCompletePage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/why-kstreak" element={<WhyKStreakPage />} />
      <Route path="/mobile" element={<MobileAppPage />} />
      <Route path="/public/analytics/:token" element={<PublicAnalyticsSnapshotPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/settings/notifications" element={<ProtectedRoute><NotificationPrefsPage /></ProtectedRoute>} />
      <Route path="/admin/kt-triggers" element={<ProtectedRoute><RoleGatedRoute allowedRoles={STAFF_ROLES}><AdminKTTriggersPage /></RoleGatedRoute></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><RoleGatedRoute allowedRoles={STAFF_ROLES}><UsersPage /></RoleGatedRoute></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/articles/*" element={<ProtectedRoute><ArticlesPage /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
      <Route path="/workspace" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
      <Route path="/voice-journal" element={<ProtectedRoute><VoiceJournalPage /></ProtectedRoute>} />
      <Route path="/expertise" element={<ProtectedRoute><ExpertiseLocatorPage /></ProtectedRoute>} />
      <Route path="/knowledge-transfer" element={<ProtectedRoute><KnowledgeTransferPage /></ProtectedRoute>} />
      <Route path="/tests" element={<ProtectedRoute><CapabilityMetricsPage /></ProtectedRoute>} />
      <Route path="/knowledge-maps" element={<ProtectedRoute><KnowledgeMappingPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsDashboardPage /></ProtectedRoute>} />
      <Route path="/teams" element={<ProtectedRoute><TeamsPage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpCenterPage /></ProtectedRoute>} />
      <Route path="/webinars" element={<ProtectedRoute><WebinarsPage /></ProtectedRoute>} />
      <Route path="/knowledge-audit" element={<ProtectedRoute><KnowledgeAuditPage /></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><CompliancePage /></ProtectedRoute>} />
      <Route path="/admin/automations" element={<ProtectedRoute><AutomationsPage /></ProtectedRoute>} />
      <Route path="/admin/automations/runs" element={<ProtectedRoute><AutomationRunsLogPage /></ProtectedRoute>} />
      <Route path="/admin/email" element={<ProtectedRoute><EmailConfigPage /></ProtectedRoute>} />
      <Route path="/certifications/pipeline" element={<ProtectedRoute><CertRenewalPipelinePage /></ProtectedRoute>} />
      <Route path="/knowledge-degradation" element={<ProtectedRoute><KnowledgeDegradationPage /></ProtectedRoute>} />
      <Route path="/knowledge-deps" element={<ProtectedRoute><KnowledgeDepsPage /></ProtectedRoute>} />
      <Route path="/knowledge-predictions" element={<ProtectedRoute><KnowledgePredictionsPage /></ProtectedRoute>} />
      <Route path="/pkm" element={<ProtectedRoute><PKMPage /></ProtectedRoute>} />
      <Route path="/ontology" element={<ProtectedRoute><OntologyPage /></ProtectedRoute>} />
      <Route path="/whats-new" element={<ProtectedRoute><WhatsNewPage /></ProtectedRoute>} />
      <Route path="/admin/changelog" element={<ProtectedRoute><AdminChangelogPage /></ProtectedRoute>} />
      <Route path="/admin/benchmarks" element={<ProtectedRoute><AdminBenchmarksPage /></ProtectedRoute>} />
      <Route path="/admin/sso" element={<ProtectedRoute><AdminSSOPage /></ProtectedRoute>} />
      <Route path="/admin/domain-migration" element={<ProtectedRoute><AdminDomainMigrationPage /></ProtectedRoute>} />
      <Route path="/hr-workflows" element={<ProtectedRoute><RoleGatedRoute allowedRoles={STAFF_ROLES}><HRWorkflowsPage /></RoleGatedRoute></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><TrainingPage /></ProtectedRoute>} />
      <Route path="/cop" element={<ProtectedRoute><CoPPage /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    registerSW();
  }, []);

  return (
    <div className="App font-sans">
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <GuidedTourProvider>
              <SystemUpdateProvider>
                <AppRouter />
                <KnowledgeAssistant />
                <PWAInstallPrompt />
                <Toaster position="top-right" richColors />
              </SystemUpdateProvider>
            </GuidedTourProvider>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
